import {
  Check as CheckIcon,
  Close as CloseIcon,
  HourglassEmpty as HourglassIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import React, { useState } from "react";
import DocumentPreview from "~/components/Document/documentPreview";
import { CommonButton } from "~/components/ui/commonButton";
import Modal from "~/components/ui/modal";
import type { KintoneSystemLogs, ProcessStatus } from "~/functions/api/kintoneSystem";
import {
  DEVELOPMENT_STEPS,
  type DevelopingJob,
  type DevelopmentStep,
  type DevelopmentStepInfo,
} from "~/interface/kintoneSystem";
import { toDisplayString } from "~/utils/customDate";
import styles from "./index.module.scss";

// 型を再エクスポート（既存の参照先との互換性維持）
export type { DevelopmentStep, DevelopmentStepInfo, DevelopingJob };
export { DEVELOPMENT_STEPS };

// ステップ番号とプロセス名のマッピング
const STEP_TO_PROCESS: Record<number, keyof KintoneSystemLogs> = {
  1: "GenerateAppSetting",
  2: "GenerateFields",
  3: "GenerateCustomizeCode",
  4: "AdjustFormLayout",
};

// アプリ情報の型
export interface AppProgressInfo {
  app_id: string;
  app_name: string;
  description?: string;
  fields: Array<{
    name: string;
    code: string;
    type: string;
  }>;
  hasCustomize: boolean;
  customizeCount?: number;
}

// ドキュメント情報の型
export interface DocumentProgressInfo {
  content: string;
  isMarkdown: boolean;
}

export type DevelopmentOverallStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "canceled"
  | "unknown";

interface DevelopmentProgressProps {
  job: DevelopingJob;
  logs?: KintoneSystemLogs;
  overallStatus?: DevelopmentOverallStatus | string;
  onCancel: () => void;
  onDocumentClick?: (documentType: string) => void;
  apps?: AppProgressInfo[];
  documents?: {
    appDesign?: DocumentProgressInfo | null;
    fieldDesign?: DocumentProgressInfo | null;
    customizeSpec?: DocumentProgressInfo | null;
  };
  lastUpdated?: string;
}

const DevelopmentProgress: React.FC<DevelopmentProgressProps> = ({
  job,
  logs,
  overallStatus,
  onCancel,
  onDocumentClick,
  documents,
  lastUpdated,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // ステップのステータスを取得（logsから直接取得）
  const getStepStatus = (stepOrder: number): ProcessStatus => {
    const processName = STEP_TO_PROCESS[stepOrder];
    if (!processName) return "queued";
    return logs?.[processName]?.status || "queued";
  };

  // 現在進行中のステップを取得
  const getCurrentStep = (): number => {
    for (const step of DEVELOPMENT_STEPS) {
      const status = getStepStatus(step.order);
      if (status === "running") {
        return step.order;
      }
    }
    // runningがなければ最初の未完了ステップ
    for (const step of DEVELOPMENT_STEPS) {
      const status = getStepStatus(step.order);
      if (status !== "succeeded") {
        return step.order;
      }
    }
    return DEVELOPMENT_STEPS.length;
  };

  const currentStep = getCurrentStep();

  const inferOverallStatusFromLogs = (): DevelopmentOverallStatus => {
    const statuses = Object.values(STEP_TO_PROCESS)
      .map((processName) => logs?.[processName]?.status)
      .filter(Boolean) as ProcessStatus[];

    if (statuses.includes("failed")) return "failed";
    if (
      statuses.length === Object.keys(STEP_TO_PROCESS).length &&
      statuses.every((s) => s === "succeeded")
    )
      return "succeeded";
    if (statuses.includes("running")) return "running";
    return "queued";
  };

  const normalizedOverallStatus = (() => {
    if (
      overallStatus === "queued" ||
      overallStatus === "running" ||
      overallStatus === "succeeded" ||
      overallStatus === "failed" ||
      overallStatus === "canceled"
    ) {
      return overallStatus;
    }
    if (typeof overallStatus === "string" && overallStatus.length > 0) return "unknown";
    return inferOverallStatusFromLogs();
  })();

  const progressStatus = (() => {
    switch (normalizedOverallStatus) {
      case "succeeded":
        return { label: "完了", variant: "succeeded", showSpinner: false };
      case "failed":
        return { label: "失敗", variant: "failed", showSpinner: false };
      case "canceled":
        return { label: "キャンセル", variant: "canceled", showSpinner: false };
      default:
        return { label: "進行中", variant: "running", showSpinner: true };
    }
  })();

  // 確認モーダルを開く
  const handleCancelClick = () => {
    setIsConfirmModalOpen(true);
  };

  // 確認モーダルを閉じる
  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  // 中止を確定
  const handleConfirmCancel = () => {
    setIsConfirmModalOpen(false);
    onCancel();
  };

  return (
    <div className={styles["developmentProgress"]}>
      {/* ヘッダー */}
      <div className={styles["header"]}>
        <div className={styles["headerInfo"]}>
          <h2 className={styles["spaceName"]}>{job.spaceName}</h2>
          {lastUpdated && (
            <p className={styles["lastUpdated"]}>最終更新: {toDisplayString(lastUpdated)}</p>
          )}
        </div>
        <button type="button" className={styles["kintoneButton"]} disabled>
          kintoneを見る
        </button>
      </div>

      {/* 生成進行状況 */}
      <div className={styles["progressSection"]}>
        <div className={styles["progressHeader"]}>
          <div className={styles["progressTitleRow"]}>
            <h3 className={styles["progressTitle"]}>生成進行状況</h3>
            <div className={styles["statusChipRow"]}>
              <span
                className={`${styles["statusChip"]} ${styles[progressStatus.variant]}`}
                data-testid="development-progress-status"
                data-status={normalizedOverallStatus}
              >
                {progressStatus.label}
              </span>
              {progressStatus.showSpinner && (
                <span className={styles["statusSpinner"]} role="img" aria-label="処理中" />
              )}
            </div>
          </div>
          <button type="button" className={styles["cancelButton"]} onClick={handleCancelClick}>
            アプリ生成を中止
          </button>
        </div>

        {/* 横型ステッパー */}
        <div className={styles["stepper"]}>
          {DEVELOPMENT_STEPS.map((step, index) => {
            const status = getStepStatus(step.order);
            const isCompleted = status === "succeeded";
            const isRunning = status === "running";
            const isFailed = status === "failed";
            const isCurrent = step.order === currentStep;
            const isLast = index === DEVELOPMENT_STEPS.length - 1;

            return (
              <React.Fragment key={step.id}>
                <div className={styles["stepItem"]}>
                  <div
                    className={`${styles["stepBadge"]} ${isCompleted ? styles["completed"] : ""} ${isRunning ? styles["running"] : ""} ${isFailed ? styles["failed"] : ""} ${isCurrent && !isRunning && !isFailed ? styles["current"] : ""}`}
                  >
                    {isCompleted ? (
                      <CheckIcon className={styles["stepCheckIcon"] ?? ""} />
                    ) : isFailed ? (
                      <CloseIcon className={styles["stepCloseIcon"] ?? ""} />
                    ) : isCurrent && !isRunning ? (
                      <HourglassIcon className={styles["stepHourglassIcon"] ?? ""} />
                    ) : (
                      <span>{step.order}</span>
                    )}
                  </div>
                  <span
                    className={`${styles["stepLabel"]} ${isCurrent ? styles["currentLabel"] : ""}`}
                  >
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <div
                    className={`${styles["stepConnector"]} ${isCompleted ? styles["completedConnector"] : ""}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ドキュメントプレビュー（マークダウン・JSON両対応） */}
      <div className={styles["documentsSection"]}>
        <h3 className={styles["sectionTitle"]}>ドキュメント</h3>
        <div className={styles["documentPreviews"]}>
          {documents?.appDesign?.content && (
            <button
              type="button"
              className={`${styles["documentPreviewCard"]} ${onDocumentClick ? styles["clickable"] : ""}`}
              onClick={() => onDocumentClick?.("app-design")}
              disabled={!onDocumentClick}
            >
              <h4 className={styles["documentTitle"]}>
                <span>アプリ設計書</span>
                {onDocumentClick && <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />}
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.appDesign.content}
                  documentType="app-design"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
          {documents?.fieldDesign?.content && (
            <button
              type="button"
              className={`${styles["documentPreviewCard"]} ${onDocumentClick ? styles["clickable"] : ""}`}
              onClick={() => onDocumentClick?.("field-design")}
              disabled={!onDocumentClick}
            >
              <h4 className={styles["documentTitle"]}>
                <span>フィールド設計書</span>
                {onDocumentClick && <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />}
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.fieldDesign.content}
                  documentType="field-design"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
          {documents?.customizeSpec?.content && (
            <button
              type="button"
              className={`${styles["documentPreviewCard"]} ${onDocumentClick ? styles["clickable"] : ""}`}
              onClick={() => onDocumentClick?.("customize-specification")}
              disabled={!onDocumentClick}
            >
              <h4 className={styles["documentTitle"]}>
                <span>カスタマイズ仕様書</span>
                {onDocumentClick && <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />}
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.customizeSpec.content}
                  documentType="customize-specification"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* 中止確認モーダル */}
      <Modal
        label="アプリ生成の中止"
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        usePortal
        content={
          <div className={styles["confirmModalContent"]}>
            <p className={styles["confirmMessage"]}>アプリ生成を中止しますか？</p>
            <p className={styles["confirmWarning"]}>中止した場合、やり直しはできません。</p>
          </div>
        }
        footer={
          <div className={styles["confirmModalFooter"]}>
            <CommonButton variant="label" onClick={handleCloseConfirmModal}>
              キャンセル
            </CommonButton>
            <CommonButton onClick={handleConfirmCancel}>中止する</CommonButton>
          </div>
        }
      />
    </div>
  );
};

export default DevelopmentProgress;
